import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import { useScreenHighlight } from "../../Hooks/ScreenHighlight";
import { HIGHLIGHT_TOOLS } from "../../utils/highlightCoords";
import HighlightOverlay from "./HighlightOverlay";
import HighlightToolbar from "./HighlightToolbar";

export default function ScreenHighlightLayer() {
  const highlight = useScreenHighlight();
  const beginStroke = highlight?.beginStroke;
  const movePointer = highlight?.movePointer;
  const endStroke = highlight?.endStroke;
  const hideLaser = highlight?.hideLaser;

  const handlePointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      beginStroke?.(event.clientX, event.clientY, event.shiftKey);
    },
    [beginStroke],
  );

  const handlePointerMove = useCallback(
    (event) => {
      movePointer?.(event.clientX, event.clientY, event.shiftKey);
    },
    [movePointer],
  );

  const handlePointerUp = useCallback(
    (event) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      endStroke?.();
    },
    [endStroke],
  );

  if (!highlight?.canHighlight) return null;

  const {
    toolbarOpen,
    tool,
    strokes,
    liveStroke,
    laser,
    overlayRef,
    isDrawingTool,
  } = highlight;

  const hasMarks = strokes.length > 0 || Boolean(liveStroke) || Boolean(laser);
  if (!hasMarks && !toolbarOpen) return null;

  const layerClass = [
    "screen-highlight-layer",
    isDrawingTool ? "is-drawing" : "",
    toolbarOpen && tool === HIGHLIGHT_TOOLS.LASER ? "is-laser" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <>
      <div className={layerClass}>
        <HighlightOverlay
          overlayRef={overlayRef}
          strokes={strokes}
          liveStroke={liveStroke}
          laser={laser}
          interactive={isDrawingTool}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={hideLaser}
        />
      </div>
      {toolbarOpen ? <HighlightToolbar /> : null}
    </>,
    document.body,
  );
}
